import { communityMemberModelToViewModel, anonymousMember } from '../../utils/member-view-model-helper';
import { IVirtualTerminalPayerViewModel } from '../payer-search/payer-view-model';
import { observable, computed, action, reaction } from 'mobx';
import { IPayerSearchResult } from '../../../components/payer-search-omnibox/payer-search-omnibox';
import {
	ICheckNumber,
	IPaymentEntryModel,
	PaymentMethod,
	RecordedCheckPaymentMethod,
	PaymentMethodUiType,
	existingPaymentMethodFactory,
	CashPaymentMethod
} from '../payment-entry/payment-entry-model';
import { VirtualTerminalListingStore } from '../../virtual-terminal-listing-store';
import { CommonPaymentFieldsModel, CustomFieldEditModel } from '../../../loggedinweb-generated';
import { paymentMethodsForListing, existingPaymentMethodsForListing } from '../../utils/payment-method-creators';
import { toPaymentMethodType } from '../../utils/payment-method-type-helper';
import {
	EndType,
	FrequencyCode,
	ScheduleFrequency,
	VirtualTerminalExistingPaymentMethodsViewModel,
	VirtualTerminalPaymentDetailsModel,
	VirtualTerminalListingConfiguration,
	AssetType,
} from '../../virtual-terminal-generated';
import { IMemberViewModel } from '../member/member-view-model';
import { StorageHelper } from '../../../helpers/storagehelper';
import { DefaultSettingsViewModel } from '../default-settings/default-settings-view-model';
import { mergeCustomFieldEditModels, getCustomFieldEditModelMap } from '../../../utils/customFieldsUtils';

let nextFormKey = 0;

export class VirtualTerminalFormViewModel {
	@observable
	omniboxValue = '';

	@observable
	checkNumber: ICheckNumber = null;

	@observable
	checkReaderState: '' | 'reading' | 'failure' = '';

	@observable
	searchValue = '';

	@observable
	searchResult: IPayerSearchResult = null;

	@observable
	payersAccociatedWithCheckResult: IPayerSearchResult = null;

	@observable
	loadMoreInProgress = false;

	@observable
	openRequested = false;

	@observable
	selectedMember: IMemberViewModel = null;

	@observable
	originalShouldSendEmailNotifications: boolean = null;

	@computed
	get selectedPayer(): IVirtualTerminalPayerViewModel {
		return this.selectedMember && communityMemberModelToViewModel(this.selectedMember);
	}

	@observable
	savePaymentInProgress = false;

	@observable
	paymentEntry: IPaymentEntryModel = null;

	@observable
	loadingExistingPaymentMethods = false;

	@observable
	loadingPaymentDetails = false;

	@observable
	amountFocused = false;

	@observable
	formKey = `${nextFormKey++}`;

	@observable
	processingRemovePaymentMethod: boolean = false;

	@observable
	validationErrors: { [key: string]: string };

	@computed
	get paymentEntryDisabled() {
		return this.loadingPaymentDetails || this.listingStore.loadingListingConfiguration || this.savePaymentInProgress;
	}

	@computed
	get paymentEntryBusy() {
		return this.loadingPaymentDetails || this.checkReaderState === 'reading' || (this.listingStore.loadingListingConfiguration && this.paymentEntryVisible);
	}

	@computed
	get paymentSearchDisabled() {
		return this.savePaymentInProgress;
	}

	@computed
	get paymentEntryVisible(): boolean {
		return !!(this.paymentEntry && this.paymentEntry.paymentMethod && this.listingStore.selectedListingConfiguration);
	}

	@computed
	get currentSearchResult() {
		return this.searchResult ? this.searchResult : this.payersAccociatedWithCheckResult;
	}

	@computed
	get checkReadingMode() {
		return !!this.checkNumber;
	}

	@computed
	get addGiftDisabled() {
		return this.paymentEntryBusy || this.selectedPayer === null;
	}

	@computed
	get selectedMemberIsAnonymous() {
		return this.selectedMember && this.selectedMember.IsAnonymous;
	}

	@computed
	get paymentEntryReferencesValidForTheCurrentMerchant() {
		return this.paymentEntry
			&& this.paymentEntry.CommonPaymentFields
			&& this.paymentEntry.CommonPaymentFields.ReferenceFieldValues.filter(x => x.Key in this.listingStore.selectedListingCustomFieldsMap);
	}

	@computed
	get existingPaymentMethods(): PaymentMethod[] {
		return (this.selectedPayer
			&& this.selectedPayer.existingPaymentMethods
			&& this.selectedPayer.existingPaymentMethods.get().PaymentMethods.map(x => existingPaymentMethodFactory(x))) || [];
	}

	@observable
	newPaymentMethods: PaymentMethod[] = [];

	@computed
	get paymentMethods(): PaymentMethod[] {
		return this.newPaymentMethods.concat(this.existingPaymentMethods);
	}

	@computed
	get hasMoreExistingPaymentMethods() {
		return (this.selectedPayer
			&& this.selectedPayer.existingPaymentMethods
			&& this.selectedPayer.existingPaymentMethods.get().HasMorePages) || false;
	}

	@computed
	private get defaultSettings() {
		return this.defaultSettingsViewModel.defaultSettingsModel;
	}
	
	annualRecurringGivingEnabled: boolean;
	semiAnnualRecurringGivingEnabled: boolean;
	quarterlyRecurringGivingEnabled: boolean;

	constructor(public listingStore: VirtualTerminalListingStore,
		private defaultSettingsViewModel: DefaultSettingsViewModel = new DefaultSettingsViewModel(listingStore, null),
		public readonly allowRecurring = true,
		public readonly allowNonCash = true) {

		reaction(() => this.paymentEntry && this.paymentEntry.paymentMethod && this.paymentEntry.paymentMethod.type, this.updateSendEmailNotificationForPaymentMethodType);
		reaction(() => this.listingStore.selectedListingConfiguration, this.applySelectedListingConfiguration, { fireImmediately: true });
	}

	@action.bound
	updateSendEmailNotificationForPaymentMethodType(paymentMethodType: PaymentMethodUiType) {
		if (paymentMethodType === PaymentMethodUiType.CreditCard
			|| paymentMethodType === PaymentMethodUiType.ExistingCreditCard) {
			this.originalShouldSendEmailNotifications = this.paymentEntry.sendEmailNotifications;
			this.paymentEntry.sendEmailNotifications = true;
		} else {
			if (!this.checkReadingMode) {
				this.paymentEntry.sendEmailNotifications = this.originalShouldSendEmailNotifications;
			}
		}
	}

	@action.bound
	applySelectedListingConfiguration(config: VirtualTerminalListingConfiguration) {
		if (!config) {
			return;
		}
		this.setNewPaymentMethods();
		this.setPaymentReferenceValues();
		// when listing does not support a payment method, selecte the first or preferred payment method
		if (this.paymentEntry && this.paymentEntry.paymentMethod) {
			if (config.SupportedPaymentMethodTypes.indexOf(toPaymentMethodType(this.paymentEntry.paymentMethod.type)) === -1) {
				this.selectPreferredPaymentMethod();
			}
		}
	}

	@action.bound
	setNewPaymentMethods() {
		const { selectedListingConfiguration: listingConfiguration } = this.listingStore;

		const listingPaymentMethods = listingConfiguration ? paymentMethodsForListing(listingConfiguration, this.allowNonCash) : [];

		if (this.checkNumber) {
			this.newPaymentMethods = listingPaymentMethods.filter(x => x.type === PaymentMethodUiType.RecordedCheck);
		} else {
			this.newPaymentMethods = listingPaymentMethods;
		}
	}

	@action.bound
	setPaymentReferenceValues() {
		if (this.paymentEntry) {
			this.paymentEntry.CommonPaymentFields.ReferenceFieldValues = this.getDefaultReferenceValues();
		}
	}

	@action.bound
	handleOmniboxValueChange(value: string) {
		this.omniboxValue = value;

		if (this.checkReaderState === 'failure') {
			this.checkReaderState = '';
		}

		if (!value || !value.trim()) {
			this.selectedMember = null;

			if (!this.checkReadingMode) {
				this.clearSearchResult();

				if (this.paymentEntryVisible) {
					this.resetPaymentDetails();
				}
			}
		}
	}

	@action.bound
	checkReadingStarted() {
		this.selectedMember = null;

		this.checkReaderState = 'reading';
		this.payersAccociatedWithCheckResult = null;
		this.clearSearchResult();
	}

	@action.bound
	checkReadingFailed() {
		this.omniboxValue = '';
		this.checkReaderState = 'failure';
	}

	@action.bound
	setLoadMoreInProgress(inProgress: boolean) {
		this.loadMoreInProgress = inProgress;
	}

	@action.bound
	searchPayersComplete(searchValue: string, searchResult: IPayerSearchResult) {
		this.searchValue = searchValue;
		this.searchResult = searchResult;
		this.loadMoreInProgress = false;
	}

	@action.bound
	searchPayersForCheckComplete(check: ICheckNumber,
		searchResult: IPayerSearchResult,
		paymentDetails: VirtualTerminalPaymentDetailsModel) {
		this.omniboxValue = '';
		this.checkReaderState = '';
		this.checkNumber = check;
		this.payersAccociatedWithCheckResult = searchResult;
		this.loadMoreInProgress = false;

		if (!searchResult.appendedResults) {
			this.populateDefaultPaymentDetails();
			const recordedCheck = this.newPaymentMethods.filter(x => x.type === PaymentMethodUiType.RecordedCheck)[0] as RecordedCheckPaymentMethod;
			const { accountNumber, routingNumber, checkNumber } = this.checkNumber;
			const partiallyMisread = !accountNumber || !routingNumber || !checkNumber;

			this.paymentEntry.paymentMethod = recordedCheck;
			this.paymentEntry.paymentMethod.applyCheckNumber(check);
			this.paymentEntry.paymentMethod.updatePartiallyMisread(partiallyMisread);
			this.paymentEntry.isRecurring = false;

			if (paymentDetails) {
				this.applyPaymentDetailsToReadCheck(paymentDetails);
			} else {
				this.applyDefaultPaymentDetailsToReadCheck();
			}

			if (this.selectedPayer && !this.paymentEntry.amount) {
				this.amountFocused = true;
			}
		}
	}

	@action.bound
	handleMenuStateChange(open: boolean) {
		this.openRequested = open;
	}

	@action.bound
	clearSearchResult() {
		this.searchResult = null;
		this.searchValue = '';
	}

	@action.bound
	selectMemberMatch(searchResult: IMemberViewModel[], matchedId: number) {
		if (matchedId === null) {
			return;
		}
		[this.selectedMember] = searchResult.filter(x => x.CommunityMemberId === matchedId);
	}

	@action.bound
	updateSelectedMember(member: IMemberViewModel) {
		this.selectedMember = member;

		this.clearSearchResult();

		// focus the amount field when there's a payer selected and no amount entered
		if (member && this.paymentEntry && this.paymentEntryVisible && !this.paymentEntry.amount) {
			this.amountFocused = true;
		}
	}

	@action.bound
	editSelectedMember(member: IMemberViewModel) {
		const existingPaymentMethods = { ...this.selectedPayer.existingPaymentMethods.get() };
		this.selectedMember = member;
		this.selectedPayer.existingPaymentMethods.set(existingPaymentMethods);

		if (this.paymentEntry && this.paymentEntryVisible && !this.paymentEntry.amount) {
			this.amountFocused = true;
		}
	}

	@action.bound
	loadExistingPaymentMethodsStarted() {
		this.loadingExistingPaymentMethods = true;
	}

	@action.bound
	loadExistingPaymentMethodsFinished(existingPaymentMethods: VirtualTerminalExistingPaymentMethodsViewModel) {
		this.loadingExistingPaymentMethods = false;
		if (this.selectedPayer) {
			const { selectedListingConfiguration } = this.listingStore;
			existingPaymentMethods.PaymentMethods = existingPaymentMethodsForListing(existingPaymentMethods.PaymentMethods, selectedListingConfiguration);
			this.selectedPayer.existingPaymentMethods.set(existingPaymentMethods);
		}
	}

	@action.bound
	loadExistingPaymentMethodsCancelled() {
		this.loadingExistingPaymentMethods = false;
	}

	@action.bound
	loadPaymentDetailsStarted(checkMode: boolean) {
		this.loadingPaymentDetails = true;
	}

	@action.bound
	loadPaymentDetailsFinished(paymentDetails: VirtualTerminalPaymentDetailsModel) {
		this.loadingPaymentDetails = false;

		const hasPaymentDetailsAndInCheckReadingMode = paymentDetails && this.checkReadingMode;

		if (hasPaymentDetailsAndInCheckReadingMode) {
			this.applyPaymentDetailsToReadCheck(paymentDetails);
		} else if (paymentDetails) {
			this.applyPaymentDetails(paymentDetails);
		} else if (this.checkReadingMode) {
			this.applyDefaultPaymentDetailsToReadCheck();
		} else {
			this.populateDefaultPaymentDetails();
		}

		if (!this.paymentEntry.paymentMethod) {
			this.selectPreferredPaymentMethod();
		}

		if (!this.paymentEntry.amount) {
			this.amountFocused = true;
		}
	}

	@action.bound
	loadPaymentDetailsCancelled() {
		this.loadingPaymentDetails = false;
	}

	@action.bound
	handleAmountChange(value: number) {
		this.paymentEntry.amount = value;
	}

	@action.bound
	setAmountFocused(focused: boolean = true) {
		this.amountFocused = focused;
	}

	@action.bound
	setAnonymousPayer() {
		this.updateSelectedMember(anonymousMember);
		this.populateDefaultPaymentDetails();
		const cashPaymentMethod = this.newPaymentMethods.filter(x => x.type === PaymentMethodUiType.Cash)[0] as CashPaymentMethod;
		this.paymentEntry.paymentMethod = cashPaymentMethod;
	}

	@action.bound
	updateReferenceFieldValue(field: CustomFieldEditModel) {
		const values = this.paymentEntry.CommonPaymentFields.ReferenceFieldValues;
		const current = values.filter(x => x.Key === field.Key)[0];

		if (current) {
			values.splice(values.indexOf(current), 1, field);
		} else {
			values.push(field);
		}
	}

	@action.bound
	updateNotes(value: string) {
		this.paymentEntry.CommonPaymentFields.Notes = value;
	}

	@action.bound
	updateGivenOn(date: Date) {
		this.paymentEntry.CommonPaymentFields.GivenOn = date;
		StorageHelper.setGivenOn(date);
	}

	@action.bound
	updatePaymentMethod(key: string) {
		const paymentMethod = this.paymentMethods.filter(x => x.key === key)[0];
		const paymentMethodType = paymentMethod.type;

		if (this.paymentEntry.isRecurring && !(paymentMethodType === PaymentMethodUiType.ACH || paymentMethodType === PaymentMethodUiType.CreditCard)) {
			this.updateIsRecurring(false);
			this.updateRecurringFrequency(`${FrequencyCode.Monthly}`);
			this.updateRecurringStartDate(this.listingStore.selectedListingConfiguration.DefaultRecurringStartDate);
			this.updateRecurringEndType(`${EndType.Never}`);
		}

		this.paymentEntry.paymentMethod = paymentMethod;
	}

	@action.bound
	updateYourId(value: string) {
		this.paymentEntry.yourId = value;
	}

	@action.bound
	updateSendEmailNotifications(sendEmailNotifications: boolean) {
		this.paymentEntry.sendEmailNotifications = sendEmailNotifications;
	}

	@action.bound
	updateIsRecurring(isRecurring: boolean) {
		this.paymentEntry.isRecurring = this.allowRecurring && isRecurring;
	}

	@action.bound
	updateRecurringFrequency(frequency: string) {
		this.paymentEntry.recurringSchedule.FrequencyCode = parseInt(frequency);
	}

	@action.bound
	updateRecurringStartDate(startDate: Date) {
		const { EndDate } = this.paymentEntry.recurringSchedule;
		this.paymentEntry.recurringSchedule.WhenNextPaymentDue = startDate;

		if (EndDate && startDate > EndDate) {
			this.updateRecurringEndDate(null);
		}
	}

	@action.bound
	updateRecurringEndType(endType: string) {
		const { recurringSchedule } = this.paymentEntry;
		const { EndDate, EndOccurrences } = recurringSchedule;
		const endTypeValue = parseInt(endType);

		switch (endTypeValue) {
			case EndType.Never:
				if (EndOccurrences) {
					this.updateRecurringEndOccurrences('');
				}
				if (EndDate) {
					this.updateRecurringEndDate(null);
				}
				break;
			case EndType.Date:
				if (EndOccurrences) {
					this.updateRecurringEndOccurrences('');
				}
				break;
			case EndType.Occurrences:
				if (EndDate) {
					this.updateRecurringEndDate(null);
				}
				break;
		}

		recurringSchedule.EndType = endTypeValue;
	}

	@action.bound
	updateRecurringEndDate(endDate: Date) {
		this.paymentEntry.recurringSchedule.EndDate = endDate;
	}

	@action.bound
	updateRecurringEndOccurrences(endOccurrences: string) {
		const value = parseInt(endOccurrences);
		let endOccurrencesValue;

		if (isNaN(value)) {
			endOccurrencesValue = '';
		} else if (value < 1) {
			endOccurrencesValue = 1;
		} else {
			endOccurrencesValue = value;
		}

		this.paymentEntry.recurringSchedule.EndOccurrences = endOccurrencesValue;
	}

	@action.bound
	savePaymentStarted() {
		this.savePaymentInProgress = true;
	}

	@action.bound
	savePaymentFinished() {
		this.savePaymentInProgress = false;
	}

	@action.bound
	removePaymentMethod(paymentMethod: PaymentMethod) {
		if (this.paymentEntry.paymentMethod === paymentMethod) {
			this.selectPreferredPaymentMethod();
		}

		let existingPaymentMethodsViewModel = this.selectedPayer.existingPaymentMethods.get();
		const paymentMethods = existingPaymentMethodsViewModel.PaymentMethods.filter(x => `${x.PaymentMethodIndex}` !== paymentMethod.key);

		this.selectedPayer.existingPaymentMethods.set({
			PaymentMethods: paymentMethods,
			HasMorePages: existingPaymentMethodsViewModel.HasMorePages
		});
	}

	@action.bound
	removePaymentMethodStarted() {
		this.processingRemovePaymentMethod = true;
	}

	@action.bound
	removePaymentMethodFinished() {
		this.processingRemovePaymentMethod = false;
	}

	@action.bound
	updateValidationErrors(validationErrors: { [key: string]: string }) {
		this.validationErrors = validationErrors;
	}

	@action.bound
	resetForm() {
		this.formKey = `${nextFormKey++}`;
		this.omniboxValue = '';
		this.checkReaderState = '';
		this.checkNumber = null;
		this.payersAccociatedWithCheckResult = null;
		this.selectedMember = null;
		this.clearSearchResult();

		this.resetPaymentDetails();
	}

	@action.bound
	resetPaymentDetails() {
		this.populateDefaultPaymentDetails();
		this.setNewPaymentMethods();
		this.selectPreferredPaymentMethod();
		this.amountFocused = false;
	}

	@action.bound
	setDescriptionForDonor(value: string) {
		this.paymentEntry.descriptionForDonor = value;
	}

	@action.bound
	setDescriptionForMerchant(value: string) {
		this.paymentEntry.descriptionForMerchant = value;
	}

	@action.bound
	setAssetType(value: AssetType) {
		this.paymentEntry.assetType = value;
	}

	@action.bound
	private populateDefaultPaymentDetails() {
			this.originalShouldSendEmailNotifications = true;
			const { DefaultPaymentMethodType } = this.defaultSettings;
			this.paymentEntry = {
				amount: null,
				CommonPaymentFields: this.getDefaultCommonPaymentFields(),
				paymentMethod: this.paymentMethods.filter(x => toPaymentMethodType(x.type) === DefaultPaymentMethodType)[0],
				paymentMethodIndex: null,
				isRecurring: false,
				yourId: null,
				sendEmailNotifications: true,
				recurringSchedule: this.getDefaultRecurringSchedule()
			};
	}

	@action.bound
	private getDefaultCommonPaymentFields() {
		const { DefaultNotes, DefaultGivenOn } = this.defaultSettings;
		return <CommonPaymentFieldsModel>{
			GivenOn: DefaultGivenOn || StorageHelper.getGivenOn(),
			Notes: DefaultNotes || '',
			ReferenceFieldValues: this.getDefaultReferenceValues(),
		};
	}

	@action.bound
	private getDefaultRecurringSchedule() {
		return <ScheduleFrequency>{
			FrequencyCode: FrequencyCode.Monthly,
			WhenNextPaymentDue: this.listingStore.selectedListingConfiguration.DefaultRecurringStartDate,
			EndType: EndType.Never,
			EndOccurrences: null,
			EndDate: null,
		};
	}

	@action.bound
	private applyPaymentDetailsToReadCheck(paymentDetails: VirtualTerminalPaymentDetailsModel) {
		if (paymentDetails.Amount !== null) {
			this.paymentEntry.amount = paymentDetails.Amount;
		}

		this.paymentEntry.yourId = paymentDetails.YourId;
		this.paymentEntry.sendEmailNotifications = paymentDetails.WantsTransactionNotifications;
		this.paymentEntry.CommonPaymentFields = this.getCommonFieldsFromPaymentDetailsAndDefaultSettings(paymentDetails);
	}

	@action.bound
	private applyPaymentDetails(paymentDetails: VirtualTerminalPaymentDetailsModel) {
		this.originalShouldSendEmailNotifications = paymentDetails.WantsTransactionNotifications;
		this.paymentEntry = {
			amount: paymentDetails.Amount !== null ? paymentDetails.Amount : null,
			CommonPaymentFields: this.getCommonFieldsFromPaymentDetailsAndDefaultSettings(paymentDetails),
			paymentMethod: !paymentDetails ? null : this.getPaymentMethod(paymentDetails),
			paymentMethodIndex: paymentDetails.PaymentMethodIndex,
			yourId: paymentDetails.YourId,
			isRecurring: false,
			sendEmailNotifications: paymentDetails.WantsTransactionNotifications,
			recurringSchedule: this.getDefaultRecurringSchedule()
		};
	}

	@action.bound
	private applyDefaultPaymentDetailsToReadCheck() {
		this.paymentEntry.amount = null;
		this.paymentEntry.yourId = null;
		this.paymentEntry.CommonPaymentFields = this.getDefaultCommonPaymentFields();
		this.paymentEntry.sendEmailNotifications = true;
	}

	@action.bound
	private selectPreferredPaymentMethod() {
		this.paymentEntry.paymentMethod = this.getPreferredPaymentMethod();
	}

	@action.bound
	private getPreferredPaymentMethod(): PaymentMethod {
		const { selectedListingConfiguration } = this.listingStore;
		const preferredPaymentMethod = this.newPaymentMethods.filter(x => toPaymentMethodType(x.type) === selectedListingConfiguration.PreferredPaymentMethodType)[0]
			|| this.newPaymentMethods[0];
		return preferredPaymentMethod;
	}

	@action.bound
	private getPaymentMethod(paymentDetails: VirtualTerminalPaymentDetailsModel) {
		const { PaymentReference, PaymentMethodIndex } = paymentDetails;
		const { DefaultPaymentMethodType } = this.defaultSettings;

		const paymentMethod = DefaultPaymentMethodType ? this.getPaymentMethodUsingDefault(PaymentMethodIndex) : this.getPaymentMethodFromDetails(paymentDetails);

		if (!paymentMethod) {
			return this.getPreferredPaymentMethod();
		}

		if (paymentMethod.type === PaymentMethodUiType.RecordedACH || paymentMethod.type === PaymentMethodUiType.RecordedCreditCard) {
			(paymentMethod as any).updatePaymentReference(PaymentReference);
		}

		return paymentMethod;
	}

	@action.bound
	private getPaymentMethodUsingDefault(paymentMethodIndex: number): PaymentMethod {
		const { DefaultPaymentMethodType } = this.defaultSettings;

		if (paymentMethodIndex !== null) {
			const existingPaymentMethod = this.tryGetExistingPaymentMethodUsingDefault(paymentMethodIndex);
			if (existingPaymentMethod) {
				return existingPaymentMethod;
			}
		}

		return this.newPaymentMethods.filter(x => toPaymentMethodType(x.type) === DefaultPaymentMethodType)[0];
	}

	@action.bound
	private tryGetExistingPaymentMethodUsingDefault(paymentMethodIndex: number): PaymentMethod {
		const { DefaultPaymentMethodType } = this.defaultSettings;
		return this.existingPaymentMethods.filter(x => x.key === `${paymentMethodIndex}` && toPaymentMethodType(x.type) === DefaultPaymentMethodType)[0];
	}

	@action.bound
	private getPaymentMethodFromDetails(paymentDetails: VirtualTerminalPaymentDetailsModel): PaymentMethod {
		if (paymentDetails.PaymentMethodIndex !== null) {
			return this.existingPaymentMethods.filter(x => x.key === `${paymentDetails.PaymentMethodIndex}`)[0];
		}
		return this.newPaymentMethods.filter(x => toPaymentMethodType(x.type) === paymentDetails.PaymentMethodType)[0];
	}

	@action.bound
	private getCommonFieldsFromPaymentDetailsAndDefaultSettings(paymentDetails: VirtualTerminalPaymentDetailsModel): CommonPaymentFieldsModel {
		const { Notes: paymentNotes, ReferenceFieldValues: paymentReferenceFieldValues } = paymentDetails;
		const { DefaultNotes, DefaultGivenOn, DefaultReferenceFields } = this.defaultSettings;

		return {
			Notes: DefaultNotes || paymentNotes,
			GivenOn: DefaultGivenOn || StorageHelper.getGivenOn(),
			ReferenceFieldValues: paymentReferenceFieldValues
				? mergeCustomFieldEditModels(
					getCustomFieldEditModelMap(paymentReferenceFieldValues),
					DefaultReferenceFields)
				: this.getDefaultReferenceValues()
		};
	}

	@action.bound
	private getDefaultReferenceValues(): CustomFieldEditModel[] {
		const { DefaultReferenceFields } = this.defaultSettings;
		return mergeCustomFieldEditModels(this.listingStore.selectedListingCustomFieldDefaultValuesMap, DefaultReferenceFields);
	}
}
